import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  Plugin,
  PointElement,
  ScriptableContext,
  Tooltip,
  TooltipLabelStyle,
} from 'chart.js'
import { getCssRgbValue } from 'shared/utils/get-css-rgb-value'

export type DashboardChartType = 'bar' | 'line'

ChartJS.register(
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
)

const getLineGradient = ({ chart }: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = chart
  const color = getCssRgbValue('--color-icon-accent', 0.1)

  if (!chartArea) return color

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)

  gradient.addColorStop(0, color)
  gradient.addColorStop(1, getCssRgbValue('--color-bg-primary', 0))

  return gradient
}

/*
 * Marker of the hovered point: an accent core inside a white ring that casts a soft shadow. Chart.js
 * point elements carry no shadow option, so the marker is drawn here and the point element itself
 * stays invisible.
 */
const MARKER_RADIUS = 5

const MARKER_RING_RADIUS = 9

const MARKER_SHADOW_COLOR = 'rgba(50, 50, 71, 0.36)'

const MARKER_SHADOW_OFFSET_Y = 2

const MARKER_SHADOW_BLUR = 2

const drawActiveMarker = (
  ctx: CanvasRenderingContext2D,
  { x, y, pixelRatio }: { pixelRatio: number; x: number; y: number },
) => {
  ctx.save()
  /*
   * Canvas leaves shadow offsets and blur out of the current transform, so on a retina canvas they
   * have to be scaled by hand to land on the sizes the design asks for.
   */
  ctx.shadowColor = MARKER_SHADOW_COLOR
  ctx.shadowOffsetY = MARKER_SHADOW_OFFSET_Y * pixelRatio
  ctx.shadowBlur = MARKER_SHADOW_BLUR * pixelRatio
  ctx.fillStyle = getCssRgbValue('--color-bg-primary')
  ctx.beginPath()
  ctx.arc(x, y, MARKER_RING_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  ctx.save()
  ctx.fillStyle = getCssRgbValue('--color-icon-accent')
  ctx.beginPath()
  ctx.arc(x, y, MARKER_RADIUS, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

export const activePointPlugin: Plugin<'line'> = {
  id: 'activePoint',
  afterDatasetsDraw: chart => {
    const activeElement = chart.getActiveElements()[0]

    if (!activeElement) return

    const { ctx, chartArea } = chart
    const { x, y } = activeElement.element

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([4, 4])
    ctx.moveTo(x, chartArea.top)
    ctx.lineTo(x, chartArea.bottom)
    ctx.strokeStyle = getCssRgbValue('--color-border-300')
    ctx.stroke()
    ctx.restore()

    drawActiveMarker(ctx, { x, y, pixelRatio: chart.currentDevicePixelRatio ?? 1 })
  },
}

export const lineChartPlugins: Array<Plugin<'line'>> = [activePointPlugin]

/*
 * Chart.js paints its own tooltip on the canvas, which cannot carry a shadow, a divider, or mixed
 * text weights. The tooltip is rendered as a DOM node next to the canvas instead, so it matches the
 * design. One node is reused per chart and lives in the canvas wrapper, which `ChartCard` keeps
 * `relative` for that reason.
 */
const TOOLTIP_CLASS_NAME = `bg-bg-primary text-text-secondary border-border-100 shadow-1 rounded-lg border px-3 py-2 text-base font-normal pointer-events-none absolute top-0 left-0 z-10 flex w-max max-w-[240px] flex-col gap-2 opacity-0 transition-[opacity,transform] duration-150 ease-out`

const TOOLTIP_OFFSET = 12

interface ChartTooltipModel {
  body: Array<{ after: string[]; before: string[]; lines: string[] }>
  caretX: number
  caretY: number
  labelColors: TooltipLabelStyle[]
  opacity: number
  title: string[]
}

const getTooltipElement = (canvas: HTMLCanvasElement) => {
  const parent = canvas.parentElement

  if (!parent) return null

  const existing = parent.querySelector<HTMLDivElement>('[data-chart-tooltip]')

  if (existing) return existing

  const element = document.createElement('div')

  element.dataset.chartTooltip = ''
  element.className = TOOLTIP_CLASS_NAME
  parent.appendChild(element)

  return element
}

const renderTooltipContent = (
  element: HTMLDivElement,
  tooltip: ChartTooltipModel,
  withMarkers: boolean,
) => {
  element.replaceChildren()

  const [title] = tooltip.title

  if (title) {
    const titleElement = document.createElement('div')

    titleElement.className = 'border-border-100 border-b pb-2 text-center'
    titleElement.textContent = title
    element.appendChild(titleElement)
  }

  /*
   * A row is whatever the chart's `beforeLabel` / `label` / `afterLabel` callbacks returned: the
   * label carries the value and reads as emphasized, everything around it stays muted. Nothing in
   * here composes or parses that text.
   */
  tooltip.body.forEach(({ after, before, lines }, index) => {
    const row = document.createElement('div')

    row.className = 'flex items-center justify-center gap-1.5'

    if (withMarkers) {
      /*
       * The marker repeats the series color the chart resolved for that element: the bar fill, or
       * the point background a line keeps for this purpose. Anything but a plain color (a gradient
       * fill) falls back to the accent.
       */
      const color = tooltip.labelColors[index]?.backgroundColor
      const marker = document.createElement('span')

      marker.className = 'size-2 shrink-0 rounded-full'
      marker.style.backgroundColor =
        typeof color === 'string' ? color : getCssRgbValue('--color-icon-accent')
      row.appendChild(marker)
    }

    const appendSegments = (segments: string[], className?: string) => {
      segments.filter(Boolean).forEach(segment => {
        const segmentElement = document.createElement('span')

        if (className) segmentElement.className = className

        segmentElement.textContent = segment
        row.appendChild(segmentElement)
      })
    }

    appendSegments(before)
    appendSegments(lines, 'text-text-primary font-medium')
    appendSegments(after)

    if (row.childElementCount) element.appendChild(row)
  })
}

/*
 * The node sits at the wrapper origin and every offset goes into one `transform`, so moving to the
 * next point animates a single property instead of `left` and `top`.
 */
const positionTooltip = (
  element: HTMLDivElement,
  chart: { height: number; width: number },
  { caretX, caretY }: ChartTooltipModel,
) => {
  const fitsRight = caretX + TOOLTIP_OFFSET + element.offsetWidth <= chart.width
  const halfHeight = element.offsetHeight / 2
  const top = Math.min(
    Math.max(caretY, halfHeight),
    Math.max(chart.height - halfHeight, halfHeight),
  )
  const left = fitsRight
    ? `${caretX + TOOLTIP_OFFSET}px`
    : `calc(${caretX - TOOLTIP_OFFSET}px - 100%)`

  element.style.transform = `translate(${left}, calc(${top}px - 50%))`
}

/* Reading layout commits the pending transform, so the next one animates from where it landed. */
const flushLayout = (element: HTMLDivElement) => element.getBoundingClientRect()

export const externalTooltipHandler = ({
  chart,
  tooltip,
}: {
  chart: {
    canvas: HTMLCanvasElement
    data: { datasets: unknown[] }
    height: number
    width: number
  }
  tooltip: ChartTooltipModel
}) => {
  const element = getTooltipElement(chart.canvas)

  if (!element) return

  if (!tooltip.opacity) {
    element.style.opacity = '0'

    return
  }

  const wasVisible = element.style.opacity === '1'

  /* A single-series chart needs no color key, so its rows drop the marker. */
  renderTooltipContent(element, tooltip, chart.data.datasets.length > 1)

  /*
   * A tooltip already on screen glides to the next point. One that is appearing is placed without
   * the transition first, so it does not fly in from wherever the pointer left it.
   */
  if (!wasVisible) {
    element.style.transitionProperty = 'opacity'
    positionTooltip(element, chart, tooltip)
    flushLayout(element)
    element.style.transitionProperty = ''
  } else {
    positionTooltip(element, chart, tooltip)
  }

  element.style.opacity = '1'
}

export const lineChartOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  /* Chart.js fades hover styles over a second by default, which reads as lag under the cursor. */
  transitions: { active: { animation: { duration: 150 } } },
  elements: {
    point: {
      /*
       * `activePointPlugin` draws the hovered marker, so the point element itself never paints.
       * `backgroundColor` is only the color the tooltip marker reads for this series.
       */
      radius: 0,
      hoverRadius: 0,
      backgroundColor: getCssRgbValue('--color-icon-accent'),
    },
    line: {
      cubicInterpolationMode: 'monotone',
      borderWidth: 2.5,
      borderColor: getCssRgbValue('--color-icon-accent'),
      backgroundColor: getLineGradient,
      fill: true,
    },
  },
  plugins: {
    tooltip: {
      enabled: false,
      /*
       * Chart.js animates `caretX` / `caretY` itself but hands them to `external` only on pointer
       * events, so an animated model leaves the node lagging wherever the pointer stopped. The
       * model jumps to the active point and the CSS transition does the gliding.
       */
      animation: false,
      external: externalTooltipHandler,
    },
    legend: { display: false },
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: getCssRgbValue('--color-text-tertiary') },
    },
    y: {
      min: 0,
      border: { display: false, dash: [4, 4] },
      grid: {
        color: getCssRgbValue('--color-border-100'),
        drawTicks: false,
      },
      ticks: { color: getCssRgbValue('--color-text-tertiary') },
    },
  },
}

export const barChartOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  transitions: { active: { animation: { duration: 150 } } },
  elements: {
    bar: {
      backgroundColor: getCssRgbValue('--color-system-primary-200'),
      hoverBackgroundColor: getCssRgbValue('--color-system-primary-500'),
      borderRadius: 3,
    },
  },
  plugins: {
    tooltip: {
      enabled: false,
      /*
       * Chart.js animates `caretX` / `caretY` itself but hands them to `external` only on pointer
       * events, so an animated model leaves the node lagging wherever the pointer stopped. The
       * model jumps to the active point and the CSS transition does the gliding.
       */
      animation: false,
      external: externalTooltipHandler,
    },
    legend: { display: false },
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: getCssRgbValue('--color-text-tertiary') },
    },
    y: {
      min: 0,
      border: { display: false, dash: [4, 4] },
      grid: {
        color: getCssRgbValue('--color-border-100'),
        drawTicks: false,
      },
      ticks: { color: getCssRgbValue('--color-text-tertiary') },
    },
  },
}

/*
 * The axis stays compact while the tooltip names the full date, so a chart passes the long titles
 * next to its labels. Anything without a title falls back to the axis label.
 */
export const withTooltipTitles = <TType extends DashboardChartType>(
  options: NonNullable<ChartOptions<TType>>,
  titles: string[],
): ChartOptions<TType> => ({
  ...options,
  plugins: {
    ...options.plugins,
    tooltip: {
      ...options.plugins?.tooltip,
      callbacks: {
        ...options.plugins?.tooltip?.callbacks,
        title: (items: Array<{ dataIndex: number; label: string }>) => {
          const [item] = items

          if (!item) return ''

          return titles[item.dataIndex] ?? item.label
        },
      },
    },
  },
})

export const getChartData = <TType extends DashboardChartType>({
  labels,
  data,
}: {
  labels: string[]
  data: number[]
}): ChartData<TType, number[], string> =>
  ({ labels, datasets: [{ data }] }) as ChartData<TType, number[], string>
